import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiBookOpen, FiDownload, FiExternalLink, FiFileText, FiFilm, FiFolder, FiHeadphones, FiRefreshCw, FiSearch } from "react-icons/fi";
import "./WorkspaceResources.css";

const videoTypes = new Set(["mp4", "m4v", "mov", "webm", "avi", "mkv", "wmv", "mpeg", "mpg"]);
const audioTypes = new Set(["mp3", "m4a", "wav", "ogg", "oga", "aac", "flac", "aiff", "wma", "opus"]);
const documentTypes = new Set(["pdf", "doc", "docx", "word", "word_x", "txt", "rtf", "ppt", "pptx", "power_point", "power_point_x"]);
const extensionAliases = { word: "doc", word_x: "docx", power_point: "ppt", power_point_x: "pptx", excel: "xls", excel_x: "xlsx" };
const knownExtensions = new Set([...videoTypes, ...audioTypes, ...documentTypes, "csv", "xls", "xlsx", "png", "jpg", "jpeg", "gif", "webp", "svg", "zip"]);
const assetFormat = asset => {
  const extension = String(asset.downloadFileName || asset.fileName || asset.name || asset.title || "").match(/\.([a-z0-9]{1,12})$/i)?.[1];
  const format = String(extension || asset.fileType || "").toLowerCase();
  return /^[a-z0-9_]{1,24}$/.test(format) ? format : "";
};
const mediaType = asset => {
  const mime = String(asset.contentType || asset.mimeType || "").toLowerCase();
  if (mime.startsWith("audio/") || audioTypes.has(assetFormat(asset))) return "audio";
  if (mime.startsWith("video/") || videoTypes.has(assetFormat(asset))) return "video";
  return "";
};
const resourceType = asset => (mediaType(asset) || asset.type === "recording") ? "recording" : asset.type;
const fileType = asset => assetFormat(asset) || mediaType(asset) || (asset.type === "recording" ? "link" : "file");
const fileLabels = { link: "Recording link", file: "File", video: "Video", audio: "Audio", word: "DOC", word_x: "DOCX", power_point: "PPT", power_point_x: "PPTX" };
const fileLabel = value => fileLabels[value] || value.toUpperCase();

const attachmentResources = (items, source) => {
  const seen = new Set();
  return items.flatMap(item => (Array.isArray(item.files) ? item.files : []).flatMap(file => {
    const id = file.contentDocumentId || (source === "phase-two" ? file.id : "");
    if (source === "plan-studio" && (!/^069[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/.test(id) || file.sessionOnly || file.attached === false)) return [];
    if (!id || !item.id || seen.has(id)) return [];
    seen.add(id);
    return [{ ...file, id: `attachment:${id}`, attachment: true,
      title: file.title || file.name || file.fileName || file.downloadFileName || "Project file",
      fileName: file.fileName || file.name || file.downloadFileName || "",
      mimeType: file.mimeType || file.contentType || (String(file.type || "").includes("/") ? file.type : ""),
      type: documentTypes.has(assetFormat(file)) ? "document" : "file",
      description: item.title || "Project attachment",
      downloadPath: `/backend/sow/${source}/${encodeURIComponent(item.id)}/files/${encodeURIComponent(id)}/download`,
    }];
  }));
};

const recordingUrl = value => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : null;
  } catch { return null; }
};

export default function WorkspaceResources({ baseUrl = "", mode = "assets", project, requestJson }) {
  const [assets, setAssets] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [formatFilter, setFormatFilter] = useState("all");
  const [documentOpen, setDocumentOpen] = useState(mode === "documentation" && project.slug === "riomax");
  const [documentLoading, setDocumentLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const frame = useRef(null);
  const requests = useRef({ epoch: 0, load: null, downloads: new Map() });
  const documentationUrl = `${baseUrl}/backend/sow/content/${encodeURIComponent(project.slug)}/documentation`;
  const hasGuide = mode === "documentation" && project.slug === "riomax";
  const attachmentSource = mode === "assets" && project.slug === "riomax"
    ? project.planStudioEnabled === true ? "plan-studio" : project.phaseTwoEnabled === true ? "phase-two" : ""
    : "";

  const load = useCallback(async () => {
    requests.current.load?.abort();
    const controller = new AbortController();
    const epoch = ++requests.current.epoch;
    requests.current.load = controller;
    setLoading(true); setError(""); setAttachmentError("");
    try {
      await Promise.all([
        (async () => {
          try {
            const payload = await requestJson("/backend/sow/assets", { signal: controller.signal });
            if (epoch !== requests.current.epoch) return;
            if (!Array.isArray(payload?.data?.assets)) throw new Error("Project assets could not be loaded.");
            setAssets(payload.data.assets);
          } catch (failure) {
            if (epoch === requests.current.epoch && failure.name !== "AbortError") {
              setAssets([]); setError(failure.message);
            }
          }
        })(),
        ...(attachmentSource ? [(async () => {
          try {
            const payload = await requestJson(`/backend/sow/${attachmentSource}`, { signal: controller.signal });
            if (epoch !== requests.current.epoch) return;
            const items = attachmentSource === "plan-studio" ? payload?.data?.workspace?.ideas : payload?.data?.items;
            if (!Array.isArray(items)) throw new Error("Missing project attachments.");
            setAttachments(attachmentResources(items, attachmentSource));
          } catch (failure) {
            if (epoch === requests.current.epoch && failure.name !== "AbortError") {
              setAttachments([]); setAttachmentError("Project attachments could not be loaded.");
            }
          }
        })()] : []),
      ]);
    } finally { if (epoch === requests.current.epoch) setLoading(false); }
  }, [requestJson, attachmentSource]);

  useEffect(() => {
    const state = requests.current;
    setAssets([]); setAttachments([]); setPending([]); setDocumentOpen(hasGuide); setDocumentLoading(true); setQuery(""); setFilter("all"); setFormatFilter("all");
    load();
    return () => {
      state.epoch++; state.load?.abort();
      state.downloads.forEach(controller => controller.abort()); state.downloads.clear();
    };
  }, [load, project.slug, hasGuide, mode]);

  useEffect(() => {
    const receive = event => {
      if (event.source === frame.current?.contentWindow && event.data?.type === "sow:session-expired") {
        window.dispatchEvent(new CustomEvent("sow:session-expired"));
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, []);

  const download = async asset => {
    if (requests.current.downloads.has(asset.id)) return;
    const controller = new AbortController();
    requests.current.downloads.set(asset.id, controller);
    setPending([...requests.current.downloads.keys()]); setError("");
    try {
      const path = asset.attachment ? asset.downloadPath : `/backend/sow/assets/${encodeURIComponent(asset.id)}/download`;
      const response = await fetch(`${baseUrl}${path}?projectSlug=${encodeURIComponent(project.slug)}`, {
        credentials: "include", cache: "no-store", signal: controller.signal, headers: { "X-SOW-Project": project.slug },
      });
      if (controller.signal.aborted) return;
      if (response.status === 401) window.dispatchEvent(new CustomEvent("sow:session-expired"));
      if (!response.ok || /text\/html|application\/json/i.test(response.headers.get("Content-Type") || "")) {
        throw new Error("The file could not be downloaded. Please retry.");
      }
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const format = assetFormat(asset);
      const extension = extensionAliases[format] || (knownExtensions.has(format) ? format : "");
      const name = asset.fileName || asset.title || "Project file";
      link.href = url; link.download = asset.downloadFileName || (extension && !name.toLowerCase().endsWith(`.${extension}`) ? `${name}.${extension}` : name);
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure.message);
    } finally {
      if (!controller.signal.aborted) {
        requests.current.downloads.delete(asset.id); setPending([...requests.current.downloads.keys()]);
      }
    }
  };

  const resources = [...assets, ...attachments].filter(asset => mode !== "documentation" || resourceType(asset) === "document");
  const formats = [...new Set(resources.map(fileType))].sort((a, b) => fileLabel(a).localeCompare(fileLabel(b)));
  const categories = [
    { id: "all", label: "All assets", icon: FiFolder },
    { id: "document", label: "Documents", icon: FiFileText },
    { id: "recording", label: "Recordings", icon: FiFilm },
    ...(filter === "file" || resources.some(asset => resourceType(asset) === "file") ? [{ id: "file", label: "Other files", icon: FiFolder }] : []),
  ];
  const rows = resources.filter(asset => (filter === "all" || resourceType(asset) === filter)
    && (formatFilter === "all" || fileType(asset) === formatFilter)
    && `${asset.title} ${asset.description || ""} ${asset.provider || ""} ${fileLabel(fileType(asset))} ${mediaType(asset)}`.toLowerCase().includes(query.trim().toLowerCase()));
  const showGuide = hasGuide && "riomax documentation administrator guide".includes(query.trim().toLowerCase());
  const resultCount = rows.length + Number(showGuide);
  const hasFilters = Boolean(query || filter !== "all" || formatFilter !== "all");
  const emptyTitle = attachmentError ? "No resources loaded for this view"
    : !query && formatFilter === "all" && filter === "recording" ? "No recordings available"
    : hasFilters ? "No matching resources" : "No resources available";

  if (documentOpen && hasGuide) return <section className="deal-desk__document" aria-label="Project documentation">
    <div className="deal-desk__toolbar">
      <button type="button" className="deal-desk__icon" aria-label="Back to documentation library" title="Back to library" onClick={() => setDocumentOpen(false)}><FiArrowLeft /></button>
      <strong>Riomax documentation</strong>
      <a className="deal-desk__button" href={documentationUrl} target="_blank" rel="noreferrer"><FiExternalLink aria-hidden="true" />Open in new tab</a>
    </div>
    {documentLoading && <div className="deal-desk__loading" role="status">Opening documentation...</div>}
    <iframe ref={frame} title="Riomax documentation" src={`${documentationUrl}?workspaceApp=documentation`} onLoad={() => setDocumentLoading(false)}
      sandbox="allow-scripts allow-modals allow-downloads allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer" />
  </section>;

  return <section className="deal-desk__resources" aria-label={mode === "documentation" ? "Documentation library" : "Project assets"}>
    <header className="deal-desk__library-header">
      <div className="deal-desk__library-title"><h1>{mode === "documentation" ? "Documentation" : "Assets"}</h1>
        {!loading && !error && <span className="deal-desk__count" aria-label={`${resultCount} resources`}>{resultCount}</span>}</div>
      <div className="deal-desk__library-controls">
      <label className="deal-desk__search"><FiSearch aria-hidden="true" /><input type="search" aria-label={mode === "documentation" ? "Search documentation" : "Search assets"}
        placeholder={mode === "documentation" ? "Search documentation" : "Search assets"} value={query} onChange={event => setQuery(event.target.value)} /></label>
      {mode === "assets" && <select aria-label="File type" value={formatFilter} onChange={event => setFormatFilter(event.target.value)}>
        <option value="all">All file types</option>{formats.map(format => <option key={format} value={format}>{fileLabel(format)}</option>)}
        {formatFilter !== "all" && !formats.includes(formatFilter) && <option value={formatFilter}>{fileLabel(formatFilter)}</option>}
      </select>}
      <button type="button" className="deal-desk__icon" title="Refresh library" aria-label="Refresh library" disabled={loading} onClick={load}><FiRefreshCw className={loading ? "sow-spin" : ""} /></button>
      </div>
    </header>
    {mode === "assets" && <div className="workspace-resources__categories" role="group" aria-label="Asset type">
      {categories.map(({ id, label, icon: Icon }) => <button key={id} type="button" aria-pressed={filter === id}
        onClick={() => setFilter(id)}><Icon aria-hidden="true" />{label}
        <span>{!loading && !error && !attachmentError ? id === "all" ? resources.length : resources.filter(asset => resourceType(asset) === id).length : ""}</span>
      </button>)}
    </div>}
    {error && <div className="deal-desk__error" role="alert">{error}<button type="button" onClick={load}>Retry</button></div>}
    {attachmentError && <div className="workspace-resources__notice" role="status">{attachmentError}
      <button type="button" className="deal-desk__icon" aria-label="Retry project attachments" title="Retry project attachments" disabled={loading} onClick={load}><FiRefreshCw /></button>
    </div>}
    <div className="deal-desk__resource-list">
    {(showGuide || rows.length > 0) && <div className="deal-desk__column-labels" aria-hidden="true"><span>Name</span><span>Type</span><span>Actions</span></div>}
    {showGuide && <article className="deal-desk__resource-row">
      <div className="deal-desk__resource-name"><span className="deal-desk__resource-icon deal-desk__resource-icon--guide"><FiBookOpen aria-hidden="true" /></span>
        <div><h2>Riomax documentation</h2><p>Administrator guide and process reference</p></div></div>
      <span className="deal-desk__resource-type">Guide</span>
      <div className="deal-desk__resource-actions"><button type="button" className="deal-desk__button deal-desk__button--primary" onClick={() => { setDocumentLoading(true); setDocumentOpen(true); }}>Open</button>
        <a className="deal-desk__icon" href={documentationUrl} target="_blank" rel="noreferrer" title="Open documentation in new tab" aria-label="Open documentation in new tab"><FiExternalLink /></a></div>
    </article>}
    {loading ? <div className="deal-desk__skeleton" role="status" aria-label="Loading project assets"><span /><span /><span /></div> : rows.map(asset => <article className="deal-desk__resource-row" key={asset.id}>
      <div className="deal-desk__resource-name"><span className={`deal-desk__resource-icon${resourceType(asset) === "recording" ? " deal-desk__resource-icon--recording" : ""}`}>
        {mediaType(asset) === "audio" ? <FiHeadphones aria-hidden="true" /> : resourceType(asset) === "recording" ? <FiFilm aria-hidden="true" /> : <FiFileText aria-hidden="true" />}</span>
      <div><h2>{asset.title}</h2><p>{asset.description || asset.provider}</p>
        {asset.type === "recording" && asset.passcode && <span className="deal-desk__passcode">Passcode <code>{asset.passcode}</code></span>}</div></div>
      <span className="deal-desk__resource-type workspace-resources__type">{mediaType(asset) === "audio" ? "Audio recording" : mediaType(asset) === "video" ? "Video recording" : resourceType(asset) === "recording" ? "Recording" : asset.type === "document" ? "Document" : "Attachment"}
        <small>{fileLabel(fileType(asset))}</small></span>
      <div className="deal-desk__resource-actions">{asset.type === "recording" && !asset.attachment
        ? recordingUrl(asset.url) ? <a className="deal-desk__button" href={recordingUrl(asset.url)} target="_blank" rel="noreferrer"><FiExternalLink aria-hidden="true" />{asset.actionLabel === "Open record" ? "Open record" : "Watch recording"}</a> : <span className="deal-desk__access-label">Link unavailable</span>
        : <button type="button" className="deal-desk__button" disabled={pending.includes(asset.id)} onClick={() => download(asset)}>
          {pending.includes(asset.id) ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}{pending.includes(asset.id) ? "Downloading..." : "Download"}</button>}</div>
    </article>)}
    </div>
    {!loading && !error && !resultCount && <div className="deal-desk__empty"><FiFolder aria-hidden="true" /><h2>{emptyTitle}</h2>
      {hasFilters && <button type="button" className="deal-desk__button" onClick={() => { setQuery(""); setFilter("all"); setFormatFilter("all"); }}>Clear filters</button>}</div>}
  </section>;
}
